/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {type ContactImport, useListBatchContactImports} from '@agent-assist/api-typescript-react-query-hooks';
import {PERMISSIONS} from '@agent-assist/common/lib/admin/rbac';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import {Box, Button, Card, Paper, Stack} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {FC, useContext, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {ErrorPage} from './error/error-page';
import ContactImportProgressBar from '../components/ContactImportProgressBar';
import CreateContactImportModal from '../components/CreateContactImportModal';
import {type LegacyDateRange as DateRange, MuiDateRangePicker} from '../components/DateAndTimeRange/DateAndTimeRange';
import {EmptyResult} from '../components/EmptyResult';
import {LoadingSpinner} from '../components/LoadingSpinner';
import {PageContainer} from '../components/PageContainer';
import type {FeatureFlagSet} from '../flags';
import {usePaginatedTableData} from '../hooks/table/table-hooks';
import {RuntimeConfigContext} from '../providers/RuntimeContextProvider';
import {formatDateCommon} from '../services/dates';
import {useUserStore} from '../stores/userStore';

const PAGE_SIZE = 1000;

interface ContactImportsProps {}

export const ContactImportsListPage: FC<ContactImportsProps> = () => {
  const permissionsMap = useUserStore((state) => state.permissionsMap);
  const navigate = useNavigate();
  const runtimeContext = useContext(RuntimeConfigContext);
  const continuousIngestionBucket = runtimeContext?.continuousIngestionBucket;
  const [isCreatingJob, setIsCreatingJob] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const {functionImportStatus} = useFlags<FeatureFlagSet>();
  const contactImports = useListBatchContactImports(
    {
      before: dateRange?.to ? dateRange.to.getTime() - dateRange.to.getTimezoneOffset() * 60 * 1000 : undefined,
      after: dateRange?.from ? dateRange.from.getTime() - dateRange.from.getTimezoneOffset() * 60 * 1000 : undefined,
      pageSize: PAGE_SIZE,
    },
    {
      retry: false,
    },
  );

  const {items, loading, pagination, refetch} = usePaginatedTableData(contactImports, 'imports', {
    pageSize: PAGE_SIZE,
  });

  const isRefetching = items.length > 0 && (loading || contactImports.isRefetching);

  const currentDateRange: [Date | null, Date | null] = useMemo(() => {
    return [dateRange?.from ? new Date(dateRange?.from) : null, dateRange?.to ? new Date(dateRange?.to) : null];
  }, [dateRange]);

  const actions = (
    <Button
      variant={'outlined'}
      onClick={() => setIsCreatingJob(true)}
      startIcon={<AddCircleOutlineIcon />}
      disabled={!permissionsMap[PERMISSIONS.IMPORT_CREATE]}
    >
      New Import
    </Button>
  );

  return (
    <PageContainer title={'Contact Imports'} actions={actions}>
      <CreateContactImportModal
        isOpen={isCreatingJob}
        onClose={() => setIsCreatingJob(false)}
        s3Url={`s3://${continuousIngestionBucket}/`}
      />
      <Stack spacing={4}>
        <Card sx={{p: 2}}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <MuiDateRangePicker
                defaultValue={currentDateRange}
                onAccept={([from, to]) =>
                  setDateRange({
                    from: from ?? undefined,
                    to: to ?? undefined,
                  })
                }
              />
              <Box sx={{ml: 2}}>{isRefetching ? <LoadingSpinner size={'sm'} /> : null}</Box>
            </Box>
            <Box>
              <Stack spacing={1} direction={'row'}>
                <Box>{pagination}</Box>
                <IconButton aria-label={'refresh'} onClick={refetch} disabled={isRefetching}>
                  <RefreshIcon />
                </IconButton>
              </Stack>
            </Box>
          </Box>
        </Card>

        <TableContainer component={Paper}>
          <Table sx={{minWidth: 650}} aria-label={'table'}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Created</TableCell>
                {functionImportStatus && <TableCell>Status</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((contactImport: ContactImport) => (
                <TableRow
                  key={contactImport.contactImportId}
                  onClick={() => navigate(`/contacts/${contactImport.contactImportId}`)}
                  hover
                  sx={{cursor: 'pointer'}}
                >
                  <TableCell>{contactImport.name}</TableCell>
                  <TableCell>{formatDateCommon(contactImport.createdAt)}</TableCell>
                  {functionImportStatus && (
                    <TableCell>
                      {typeof contactImport.processedCount === 'number' &&
                      typeof contactImport.totalCount === 'number' ? (
                        <ContactImportProgressBar
                          processedCount={contactImport.processedCount}
                          totalCount={contactImport.totalCount}
                          status={contactImport.status}
                        />
                      ) : null}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {contactImports.isError ? (
          <ErrorPage errors={[contactImports.error]} />
        ) : items.length === 0 ? (
          <div className={'flex flex-1 w-full items-center justify-center py-10'}>
            {contactImports.status === 'loading' ? (
              <LoadingSpinner />
            ) : (
              <EmptyResult text={'No contact imports to display'} />
            )}
          </div>
        ) : null}
      </Stack>
    </PageContainer>
  );
};

export default ContactImportsListPage;
